/**
 * The interactive contents of a report-table cell.
 *
 * A report table is always an aggregate — `aggregations` is required on
 * `NativeReportQuery` — so a row is a GROUP, never a record. That rules out a
 * whole-row click: the row has no single record to open, and a row grouped by
 * both Owner and Company contains two different entities. Interaction is
 * therefore dispatched per cell, by what the cell actually is:
 *
 *   - **measure** (`is_aggregation`) → drill: show the records that compose
 *     this number. This is the rule Looker, Metabase and Omni state almost
 *     identically ("drilling into a measure shows the row-level data about the
 *     items that comprise that measure"), and Pipedrive's report tables do the
 *     same on "a number in a table".
 *   - **entity dimension** (`group_raw[key].kind === 'entity'`) → open that
 *     record. Metabase is the one product that does this without per-column
 *     configuration, inferring it from the entity-key semantic type; our
 *     `group_raw` gives us the same signal for free.
 *   - **anything else** → inert, and therefore still selectable text.
 *
 * Keeping the non-interactive cells inert is not a shortcut, it is the point:
 * every surveyed product that kept cell text selectable moved drill off a bare
 * left-click, because the two compete for the same gesture. Restricting the
 * click to the two cell types that mean something leaves labels copyable.
 */

import { useCallback } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { EntityBadgeById } from '@/lib/crm/components/badges/EntityBadgeById';
import type { ChartDatapointSelection } from '@/types/reports';

interface DrillValueButtonProps {
  /** Formatted cell text. */
  display: string;
  /** Row this cell sits in — the drill is scoped to the row's group values. */
  rowIndex: number;
  /** Measure column key. Drives chart highlight; the drill itself is row-scoped. */
  seriesKey: string;
  onDrill: (selection: ChartDatapointSelection) => void;
}

/**
 * A measure value that opens the drill panel.
 *
 * `link-gray` is the design system's inline text-action treatment: no padding,
 * and an underline that appears on hover. That hover underline is deliberate —
 * across the nine BI products surveyed, NOT ONE documents a per-cell affordance
 * for a drillable cell, and Tableau's own docs concede its action buttons stay
 * invisible until after the click. This is a gap to beat rather than a pattern
 * to copy.
 *
 * The callback is owned here rather than built inline in the parent's map, so
 * each cell gets one stable reference instead of a new closure per render.
 */
export function DrillValueButton({
  display,
  rowIndex,
  seriesKey,
  onDrill,
}: DrillValueButtonProps) {
  const handleClick = useCallback(() => {
    onDrill({ rowIndex, seriesKey });
  }, [onDrill, rowIndex, seriesKey]);

  return (
    <Button
      color='link-gray'
      size='sm'
      onClick={handleClick}
      // Inherit the cell's colour and alignment: this is a value that happens
      // to be actionable, not a control sitting in a cell.
      className='text-[var(--color-text-primary)]! tabular-nums'
      aria-label={`View the records behind ${display}`}
    >
      {display}
    </Button>
  );
}

interface EntityValueBadgeProps {
  entityId: string;
  orgId: number;
}

/**
 * A dimension value that IS a record — grouped by Owner, Company, and so on.
 *
 * `EntityBadgeById` resolves the id through the CRM store, so the cell shows the
 * record's live display name rather than the raw uuid `resolveLabel` would
 * otherwise be left holding, and it opens the record slideout on click by
 * default. Rolling a link here would duplicate a component the badge system
 * exists to prevent duplicating.
 *
 * `record` — the primary-identifier variant — rather than the compact `xs` a
 * table cell would normally take. A report table is a GROUP BY, so the row is
 * about its group: in `SUM(amount) BY company` the company cell is what the row
 * IS, not a record it happens to reference. That is the same job the first
 * column of the Contacts or Companies list does, so it gets the same weight —
 * 24px avatar, bold label, no pill. The measures beside it stay plain, which is
 * the same contrast those lists already draw.
 *
 * `avatar` is on for the same reason: the point of the variant is the record's
 * own face — a company's logo, a person's photo or initials — and without it
 * the 24px slot renders a generic type glyph instead.
 */
function EntityValueBadge({ entityId, orgId }: EntityValueBadgeProps) {
  return (
    <EntityBadgeById
      entityId={entityId}
      orgId={orgId}
      variant='record'
      avatar
    />
  );
}

interface ReportTableCellContentProps {
  /** Formatted cell text. */
  display: string;
  /** Row index, for a drill selection. */
  rowIndex: number;
  /** Column key — the measure key on a drill selection. */
  columnKey: string;
  /** This column is an aggregation (`is_aggregation`). */
  isMeasure: boolean;
  /** This row carries group descriptors, so a drill can resolve a segment. */
  canDrillRow: boolean;
  /** Set when this cell's group value is a CRM record. */
  entityId?: string;
  /** Absent when the surface did not opt into interaction. */
  interaction?: {
    orgId: number;
    onDrill: (selection: ChartDatapointSelection) => void;
  };
}

/**
 * Picks one of the three cell renderings. Written as an exhaustive ladder in a
 * component rather than a ternary chain at the call site so each branch stays
 * legible and the "inert" case is explicit rather than a fallthrough.
 */
export function ReportTableCellContent({
  display,
  rowIndex,
  columnKey,
  isMeasure,
  canDrillRow,
  entityId,
  interaction,
}: ReportTableCellContentProps) {
  if (!interaction) {
    return display;
  }
  if (entityId !== undefined) {
    return <EntityValueBadge entityId={entityId} orgId={interaction.orgId} />;
  }
  if (isMeasure && canDrillRow) {
    return (
      <DrillValueButton
        display={display}
        rowIndex={rowIndex}
        seriesKey={columnKey}
        onDrill={interaction.onDrill}
      />
    );
  }
  return display;
}
